import { z } from "zod";

/**
 * DOĞRULAMA CÜMLELERİ TÜRKÇE VE SADE (9 Ekim 2026, 4. tur — filo ajanının bulgusu).
 *
 * Şemanın kendi cümlesi olmayan her kuralda Zod'un varsayılanı İngilizceydi ve alanın altına
 * olduğu gibi düşüyordu: «Too small: expected string to have >=11 characters». Zod'un Türkçe paketi
 * de teknik konuşuyor («Çok küçük: beklenen string >=3 karakter», «beklenen int, alınan number»).
 * Burada kişinin anlayacağı cümle kuruluyor; tanınmayan durum paketin Türkçesine düşüyor.
 *
 * ÖNCELİK DEĞİŞMİYOR: şemada yazılmış özel cümle (`.min(5, "Gerekçe en az 5 karakter")`) her
 * zaman kazanır — Zod küresel işlevi yalnız cümlesi olmayan kurala sorar.
 *
 * Ön uçta formların kendi şemaları için (sunucu da aynı haritayı kuruyor:
 * `altek-erp-backend/src/core/validation/turkish-errors.ts` — iki paket ayrı, metin aynı tutulmalı).
 * `zod-jitless` gibi YÜKLENİNCE kuruluyor: giriş dosyası, tezgah ve test kurulumu içe aktarıyor.
 */
type Issue = z.core.$ZodRawIssue;

const TYPE_SENTENCES: Readonly<Record<string, string>> = {
  string: "Metin olmalı.",
  number: "Sayı olmalı.",
  int: "Tam sayı olmalı.",
  bigint: "Tam sayı olmalı.",
  boolean: "Evet ya da hayır olmalı.",
  date: "Geçerli bir tarih olmalı.",
  array: "Liste olmalı.",
  object: "Geçersiz değer.",
};

const FORMAT_SENTENCES: Readonly<Record<string, string>> = {
  email: "Geçerli bir e-posta adresi girin.",
  uuid: "Geçerli bir kayıt seçin.",
  guid: "Geçerli bir kayıt seçin.",
  cuid: "Geçerli bir kayıt seçin.",
  cuid2: "Geçerli bir kayıt seçin.",
  ulid: "Geçerli bir kayıt seçin.",
  date: "Geçerli bir tarih girin.",
  datetime: "Geçerli bir tarih ve saat girin.",
  time: "Geçerli bir saat girin.",
  url: "Geçerli bir adres girin.",
  e164: "Geçerli bir telefon numarası girin.",
  regex: "Biçimi geçersiz.",
};

/** Sınırın kendisi — yalnız sayı; tarih sınırı gibi başka bir şey cümleye girmiyor. */
function count(value: unknown): string {
  return typeof value === "bigint" || typeof value === "number" ? String(value) : "";
}

export function turkishIssueMessage(issue: Issue): string | undefined {
  switch (issue.code) {
    case "invalid_type": {
      if (issue.input === undefined) return "Bu alan zorunlu.";
      if (issue.input === null) return "Bu alan boş bırakılamaz.";
      return TYPE_SENTENCES[String(issue.expected)];
    }
    case "too_small": {
      const min = count(issue.minimum);
      if (issue.origin === "string") {
        return min === "1" ? "Bu alan boş bırakılamaz." : `En az ${min} karakter olmalı.`;
      }
      if (issue.origin === "number" || issue.origin === "int" || issue.origin === "bigint") {
        if (issue.inclusive === false) return min === "0" ? "Sıfırdan büyük olmalı." : `${min} sayısından büyük olmalı.`;
        return `En az ${min} olmalı.`;
      }
      if (issue.origin === "array" || issue.origin === "set") {
        return min === "1" ? "En az bir seçim yapın." : `En az ${min} seçim yapın.`;
      }
      return undefined;
    }
    case "too_big": {
      const max = count(issue.maximum);
      if (issue.origin === "string") return `En fazla ${max} karakter olabilir.`;
      if (issue.origin === "number" || issue.origin === "int" || issue.origin === "bigint") {
        if (issue.inclusive === false) return `${max} sayısından küçük olmalı.`;
        return `En fazla ${max} olabilir.`;
      }
      if (issue.origin === "array" || issue.origin === "set") return `En fazla ${max} seçim yapılabilir.`;
      return undefined;
    }
    case "invalid_format":
      return FORMAT_SENTENCES[String(issue.format)];
    case "invalid_value":
      return "Geçerli bir seçenek seçin.";
    case "not_multiple_of":
      return `${count(issue.divisor)} sayısının katı olmalı.`;
    default:
      return undefined;
  }
}

/** Paketin Türkçesi yedek, bizim cümle önde. Zod'un küresel ayarı birleşerek yazılıyor. */
z.config(z.locales.tr());
z.config({ customError: (issue) => turkishIssueMessage(issue) });
